import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import {
  appointmentOverlaps,
  buildAppointmentView,
  calculateOwnerStats,
  groupRevenueByMonth,
  inferLastVisit,
  topServices,
} from "@/lib/booking";
import { defaultBusinessHours } from "@/lib/defaults";
import { mapRowsToState } from "@/lib/supabaseMappers";
import { hasSupabaseEnv, supabase } from "@/lib/supabase";
import { sendWelcomeEmail } from "@/lib/welcomeEmails";
import type {
  AppState,
  Appointment,
  Business,
  BusinessSettings,
  Client,
  Service,
  StaffMember,
  User,
  UserRole,
} from "@/lib/types";

interface BookingInput {
  serviceId: string;
  staffId: string;
  date: string;
  startMinutes: number;
  name: string;
  email: string;
  phone: string;
  appointmentId?: string;
}

interface StaffInput {
  email: string;
  name: string;
  role: string;
  commissionRate: number;
  availability: string;
  specialties: string[];
  pin?: string;
}

interface AppContextValue {
  state: AppState;
  currentUser: User | null;
  currentBusiness: Business | null;
  currentSettings: BusinessSettings | null;
  loading: boolean;
  configError: string | null;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<{ ok: boolean; message?: string; role?: UserRole }>;
  signup: (input: {
    role: UserRole;
    name: string;
    email: string;
    password: string;
    businessId?: string;
    staffMemberId?: string;
    invitationToken?: string;
    emailRedirectTo?: string;
  }) => Promise<{ ok: boolean; message?: string; role?: UserRole; emailConfirmationRequired?: boolean }>;
  logout: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<{ ok: boolean; message: string }>;
  updatePassword: (password: string) => Promise<{ ok: boolean; message: string }>;
  bookAppointment: (input: BookingInput) => Promise<{ ok: boolean; message: string }>;
  cancelAppointment: (appointmentId: string) => Promise<void>;
  updateAppointmentStatus: (appointmentId: string, status: Appointment["status"]) => Promise<void>;
  addService: (input: Omit<Service, "id" | "businessId">) => Promise<void>;
  updateService: (serviceId: string, patch: Partial<Omit<Service, "id" | "businessId">>) => Promise<void>;
  deleteService: (serviceId: string) => Promise<void>;
  addStaff: (input: StaffInput) => Promise<void>;
  updateStaff: (staffId: string, patch: Partial<StaffInput>) => Promise<void>;
  deleteStaff: (staffId: string) => Promise<void>;
  clockStaff: (staffId: string, pin: string) => Promise<{ ok: boolean; message: string; eventType?: "clock_in" | "clock_out" }>;
  updateClientNotes: (clientId: string, notes: string) => Promise<void>;
  updateBusinessProfile: (patch: Partial<Omit<Business, "id" | "ownerUserId">>) => Promise<void>;
  updateBusinessSettings: (patch: Partial<Omit<BusinessSettings, "businessId">>) => Promise<void>;
}

const emptyState: AppState = {
  users: [],
  businesses: [],
  services: [],
  staff: [],
  clients: [],
  appointments: [],
  settings: [],
  subscriptions: [],
};

const AppContext = createContext<AppContextValue | null>(null);

function createId() {
  return crypto.randomUUID();
}

function wait(ms: number) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function hashStaffPin(pin: string) {
  const payload = new TextEncoder().encode(pin);
  const digest = await crypto.subtle.digest("SHA-256", payload);
  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

async function sendStaffInviteEmail(input: {
  email: string;
  staffName: string;
  businessName: string;
  acceptUrl: string;
}) {
  const { error } = await supabase.functions.invoke("send-staff-invite", {
    body: input,
  });

  if (error) {
    throw error;
  }
}

async function loadBusinessState(userId: string): Promise<AppState> {
  const { data: profile, error: profileError } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .single();

  if (profileError) {
    throw profileError;
  }

  const businessId = profile.business_id;
  if (!businessId) {
    return {
      users: [mapRowsToState({ profiles: [profile], businesses: [], services: [], staff_members: [], clients: [], appointments: [], business_settings: [], subscriptions: [] }).users[0]],
      businesses: [],
      services: [],
      staff: [],
      clients: [],
      appointments: [],
      settings: [],
      subscriptions: [],
    };
  }

  const [
    businessesResult,
    servicesResult,
    staffResult,
    clientsResult,
    appointmentsResult,
    settingsResult,
  ] = await Promise.all([
    supabase.from("businesses").select("*").eq("id", businessId),
    supabase.from("services").select("*").eq("business_id", businessId).order("name"),
    supabase
      .from("staff_members")
      .select("id, business_id, user_id, email, name, role, commission_rate, availability, specialties, is_clocked_in, clocked_in_at")
      .eq("business_id", businessId)
      .order("name"),
    supabase.from("clients").select("*").eq("business_id", businessId).order("name"),
    supabase.from("appointments").select("*").eq("business_id", businessId).order("date").order("start_minutes"),
    supabase.from("business_settings").select("*").eq("business_id", businessId),
  ]);

  const firstError = [
    businessesResult.error,
    servicesResult.error,
    staffResult.error,
    clientsResult.error,
    appointmentsResult.error,
    settingsResult.error,
  ].find(Boolean);

  if (firstError) {
    throw firstError;
  }

  return mapRowsToState({
    profiles: [profile],
    businesses: businessesResult.data ?? [],
    services: servicesResult.data ?? [],
    staff_members: staffResult.data ?? [],
    clients: clientsResult.data ?? [],
    appointments: appointmentsResult.data ?? [],
    business_settings: settingsResult.data ?? [],
    subscriptions: [],
  });
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>(emptyState);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);

  const ensureConfirmedWelcomeEmail = useCallback(async (nextState: AppState, nextSession: Session) => {
    const nextUser = nextState.users.find((entry) => entry.id === nextSession.user.id);

    if (!nextUser || nextUser.role === "staff" || nextUser.welcomeEmailSentAt || !nextSession.user.email_confirmed_at) {
      return;
    }

    try {
      const sentAt = new Date().toISOString();
      const { data, error } = await supabase
        .from("profiles")
        .update({ welcome_email_sent_at: sentAt })
        .eq("id", nextSession.user.id)
        .is("welcome_email_sent_at", null)
        .select("id");

      if (error) {
        throw error;
      }

      if (!data || data.length === 0) {
        return;
      }

      await sendWelcomeEmail({
        email: nextUser.email,
        name: nextUser.displayName,
        role: nextUser.role,
      });

      setState((current) => ({
        ...current,
        users: current.users.map((entry) =>
          entry.id === nextSession.user.id
            ? { ...entry, welcomeEmailSentAt: sentAt }
            : entry,
        ),
      }));
    } catch (error) {
      console.warn("Confirmed welcome email could not be sent.", error);
    }
  }, []);

  const hydrateSession = useCallback(async (nextSession: Session | null) => {
    setSession(nextSession);

    if (!nextSession?.user?.id) {
      setState(emptyState);
      setLoading(false);
      return null;
    }

    try {
      setLoading(true);
      setState(emptyState);
      let nextState: AppState | null = null;
      let lastError: unknown = null;

      for (const delayMs of [0, 250, 750]) {
        if (delayMs > 0) {
          await wait(delayMs);
        }

        try {
          nextState = await loadBusinessState(nextSession.user.id);
          break;
        } catch (error) {
          lastError = error;
        }
      }

      if (!nextState) {
        throw lastError;
      }

      setState(nextState);
      void ensureConfirmedWelcomeEmail(nextState, nextSession);
      setConfigError(null);
      return nextState;
    } catch (error) {
      console.error(error);
      setConfigError("Unable to load data from Supabase. Check your schema, RLS policies, and env values.");
      setState(emptyState);
      return null;
    } finally {
      setLoading(false);
    }
  }, [ensureConfirmedWelcomeEmail]);

  const refresh = useCallback(async () => {
    if (!hasSupabaseEnv) {
      setConfigError("Supabase is not configured yet. Add VITE_SUPABASE_ANON_KEY to connect this app.");
      setLoading(false);
      return;
    }

    const currentUserId = session?.user.id;
    if (!currentUserId) {
      setState(emptyState);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const nextState = await loadBusinessState(currentUserId);
      setState(nextState);
      setConfigError(null);
    } catch (error) {
      console.error(error);
      setConfigError("Unable to load data from Supabase. Check your schema, RLS policies, and env values.");
    } finally {
      setLoading(false);
    }
  }, [session?.user.id]);

  useEffect(() => {
    if (!hasSupabaseEnv) {
      setConfigError("Supabase is not configured yet. Add VITE_SUPABASE_ANON_KEY to connect this app.");
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data }) => {
      void hydrateSession(data.session);
    });

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      void hydrateSession(nextSession);
    });

    return () => {
      data.subscription.unsubscribe();
    };
  }, [hydrateSession]);

  const currentUser = useMemo(
    () => (session ? state.users.find((entry) => entry.id === session.user.id) ?? null : null),
    [session, state.users],
  );

  const currentBusiness = useMemo(() => {
    if (!currentUser) return null;
    if (currentUser.businessId) {
      return state.businesses.find((entry) => entry.id === currentUser.businessId) ?? null;
    }
    const client = state.clients.find((entry) => entry.id === currentUser.clientId);
    return client ? state.businesses.find((entry) => entry.id === client.businessId) ?? null : null;
  }, [currentUser, state.businesses, state.clients]);

  const currentSettings = useMemo(() => {
    if (!currentBusiness) return null;
    return state.settings.find((entry) => entry.businessId === currentBusiness.id) ?? null;
  }, [currentBusiness, state.settings]);

  const login = useCallback(async (email: string, password: string) => {
    if (!hasSupabaseEnv) {
      return { ok: false, message: "Supabase env vars are missing." };
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (error || !data.user) {
      return { ok: false, message: error?.message ?? "Incorrect email or password." };
    }

    const nextState = await hydrateSession(data.session);
    const nextUser = nextState?.users.find((entry) => entry.id === data.user.id);
    const fallbackRole = data.user.user_metadata?.role as UserRole | undefined;
    return { ok: true, role: (nextUser?.role as UserRole | undefined) ?? fallbackRole };
  }, [hydrateSession]);

  const signup = useCallback(async (input: {
    role: UserRole;
    name: string;
    email: string;
    password: string;
    businessId?: string;
    staffMemberId?: string;
    invitationToken?: string;
    emailRedirectTo?: string;
  }) => {
    if (!hasSupabaseEnv) {
      return { ok: false, message: "Supabase env vars are missing." };
    }

    const name = input.name.trim();
    const email = input.email.trim().toLowerCase();
    const password = input.password.trim();

    if (!name || !email || !password) {
      return { ok: false, message: "Please fill out all required fields." };
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: input.emailRedirectTo ?? `${window.location.origin}/login`,
        data: {
          role: input.role,
          display_name: name,
          business_name: input.role === "owner" ? name : null,
          business_id: input.businessId ?? null,
          staff_member_id: input.staffMemberId ?? null,
          invitation_token: input.invitationToken ?? null,
        },
      },
    });
    if (error || !data.user) {
      return { ok: false, message: error?.message ?? "Unable to create account." };
    }

    if (typeof window !== "undefined") {
      window.sessionStorage.setItem("schedora.welcomeRole", input.role);
      window.sessionStorage.setItem("schedora.welcomeName", name);
    }

    if (data.session) {
      await hydrateSession(data.session);
    } else {
      setState(emptyState);
    }

    return {
      ok: true,
      role: input.role,
      emailConfirmationRequired: !data.session,
      message: !data.session ? "Check your inbox to confirm your email before signing in." : undefined,
    };
  }, [hydrateSession]);

  const logout = useCallback(async () => {
    setState(emptyState);
    setSession(null);
    await supabase.auth.signOut();
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    if (!hasSupabaseEnv) {
      return { ok: false, message: "Supabase env vars are missing." };
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      return { ok: false, message: error.message };
    }
    return { ok: true, message: "Reset email requested successfully." };
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      return { ok: false, message: error.message };
    }
    return { ok: true, message: "Password updated successfully." };
  }, []);

  const bookAppointment = useCallback(async (input: BookingInput) => {
    if (!currentBusiness) {
      return { ok: false, message: "No business is available for booking." };
    }

    const service = state.services.find((entry) => entry.id === input.serviceId);
    if (!service) {
      return { ok: false, message: "Please choose a valid service." };
    }

    const conflicts = state.appointments.some(
      (appointment) =>
        appointment.staffId === input.staffId &&
        appointment.date === input.date &&
        appointmentOverlaps(appointment, input.startMinutes, service.duration, input.appointmentId),
    );

    if (conflicts) {
      return { ok: false, message: "That time is no longer available. Please choose another slot." };
    }

    let client = state.clients.find(
      (entry) => entry.businessId === currentBusiness.id && entry.email.toLowerCase() === input.email.trim().toLowerCase(),
    );

    if (!client) {
      const newClientId = createId();
      const { error: clientError } = await supabase.from("clients").insert({
        id: newClientId,
        business_id: currentBusiness.id,
        name: input.name.trim(),
        email: input.email.trim().toLowerCase(),
        phone: input.phone.trim(),
        notes: "",
        total_bookings: 0,
      });

      if (clientError) {
        return { ok: false, message: clientError.message };
      }

      client = {
        id: newClientId,
        businessId: currentBusiness.id,
        name: input.name.trim(),
        email: input.email.trim().toLowerCase(),
        phone: input.phone.trim(),
        notes: "",
        lastVisit: null,
        totalBookings: 0,
      };
    } else {
      await supabase.from("clients").update({
        name: input.name.trim(),
        phone: input.phone.trim(),
      }).eq("id", client.id);
    }

    if (input.appointmentId) {
      const { error } = await supabase.from("appointments").update({
        service_id: input.serviceId,
        staff_id: input.staffId,
        date: input.date,
        start_minutes: input.startMinutes,
        duration: service.duration,
        status: "confirmed",
        updated_at: new Date().toISOString(),
      }).eq("id", input.appointmentId);

      if (error) {
        return { ok: false, message: error.message };
      }
    } else {
      const { error } = await supabase.from("appointments").insert({
        business_id: currentBusiness.id,
        client_id: client.id,
        service_id: input.serviceId,
        staff_id: input.staffId,
        date: input.date,
        start_minutes: input.startMinutes,
        duration: service.duration,
        status: "confirmed",
      });

      if (error) {
        return { ok: false, message: error.message };
      }
    }

    const nextAppointments = [...state.appointments.filter((entry) => entry.clientId === client.id)];
    const nextBookingCount = nextAppointments.filter((entry) => entry.status !== "cancelled").length + (input.appointmentId ? 0 : 1);
    await supabase
      .from("clients")
      .update({ total_bookings: nextBookingCount, last_visit: inferLastVisit(nextAppointments.filter((entry) => entry.status === "completed")) })
      .eq("id", client.id);

    await refresh();
    return { ok: true, message: input.appointmentId ? "Appointment updated." : "Appointment booked." };
  }, [currentBusiness, refresh, state.appointments, state.clients, state.services]);

  const cancelAppointment = useCallback(async (appointmentId: string) => {
    await supabase.from("appointments").update({
      status: "cancelled",
      updated_at: new Date().toISOString(),
    }).eq("id", appointmentId);
    await refresh();
  }, [refresh]);

  const updateAppointmentStatus = useCallback(async (appointmentId: string, status: Appointment["status"]) => {
    await supabase.from("appointments").update({
      status,
      updated_at: new Date().toISOString(),
    }).eq("id", appointmentId);
    await refresh();
  }, [refresh]);

  const addService = useCallback(async (input: Omit<Service, "id" | "businessId">) => {
    if (!currentBusiness) return;
    await supabase.from("services").insert({
      business_id: currentBusiness.id,
      name: input.name,
      category: input.category,
      duration: input.duration,
      price: input.price,
      active: input.active,
      description: input.description,
    });
    await refresh();
  }, [currentBusiness, refresh]);

  const updateService = useCallback(async (serviceId: string, patch: Partial<Omit<Service, "id" | "businessId">>) => {
    await supabase.from("services").update({
      name: patch.name,
      category: patch.category,
      duration: patch.duration,
      price: patch.price,
      active: patch.active,
      description: patch.description,
    }).eq("id", serviceId);
    await refresh();
  }, [refresh]);

  const deleteService = useCallback(async (serviceId: string) => {
    await supabase.from("services").delete().eq("id", serviceId);
    await refresh();
  }, [refresh]);

  const addStaff = useCallback(async (input: StaffInput) => {
    if (!currentBusiness) return;
    const pinHash = input.pin ? await hashStaffPin(input.pin) : null;
    const staffId = createId();
    const staffEmail = input.email.trim().toLowerCase();
    const { error: insertError } = await supabase.from("staff_members").insert({
      id: staffId,
      business_id: currentBusiness.id,
      email: staffEmail,
      name: input.name,
      role: input.role,
      commission_rate: input.commissionRate,
      availability: input.availability,
      specialties: input.specialties,
      staff_pin_hash: pinHash,
    });

    if (insertError) {
      throw insertError;
    }

    const invitationToken = createId();
    const { error: inviteError } = await supabase.from("staff_invitations").insert({
      business_id: currentBusiness.id,
      staff_member_id: staffId,
      email: staffEmail,
      token: invitationToken,
    });

    if (inviteError) {
      throw inviteError;
    }

    await sendStaffInviteEmail({
      email: staffEmail,
      staffName: input.name,
      businessName: currentBusiness.name,
      acceptUrl: `${window.location.origin}/staff/accept?token=${invitationToken}`,
    });

    await refresh();
  }, [currentBusiness, refresh]);

  const updateStaff = useCallback(async (staffId: string, patch: Partial<StaffInput>) => {
    const nextPatch: Record<string, unknown> = {
      email: patch.email?.trim().toLowerCase(),
      name: patch.name,
      role: patch.role,
      commission_rate: patch.commissionRate,
      availability: patch.availability,
      specialties: patch.specialties,
    };

    if (patch.pin) {
      nextPatch.staff_pin_hash = await hashStaffPin(patch.pin);
    }

    await supabase.from("staff_members").update(nextPatch).eq("id", staffId);
    await refresh();
  }, [refresh]);

  const deleteStaff = useCallback(async (staffId: string) => {
    await supabase.from("staff_members").delete().eq("id", staffId);
    await refresh();
  }, [refresh]);

  const clockStaff = useCallback(async (staffId: string, pin: string) => {
    const { data, error } = await supabase.rpc("clock_staff_member", {
      target_staff_id: staffId,
      attempt_pin: pin,
    });

    if (error) {
      return { ok: false, message: error.message };
    }

    const result = Array.isArray(data) ? data[0] : data;
    if (!result?.success) {
      return { ok: false, message: result?.message ?? "Unable to verify staff PIN." };
    }

    await refresh();
    return {
      ok: true,
      message: result.message as string,
      eventType: result.event_type as "clock_in" | "clock_out",
    };
  }, [refresh]);

  const updateClientNotes = useCallback(async (clientId: string, notes: string) => {
    await supabase.from("clients").update({ notes }).eq("id", clientId);
    await refresh();
  }, [refresh]);

  const updateBusinessProfile = useCallback(async (patch: Partial<Omit<Business, "id" | "ownerUserId">>) => {
    if (!currentBusiness) return;
    await supabase.from("businesses").update({
      name: patch.name,
      email: patch.email,
      phone: patch.phone,
      address: patch.address,
    }).eq("id", currentBusiness.id);

    if (patch.name && currentUser) {
      await supabase.from("profiles").update({ display_name: patch.name }).eq("id", currentUser.id);
    }
    await refresh();
  }, [currentBusiness, currentUser, refresh]);

  const updateBusinessSettings = useCallback(async (patch: Partial<Omit<BusinessSettings, "businessId">>) => {
    if (!currentBusiness || !currentSettings) return;
    await supabase.from("business_settings").update({
      cancellation_window_hours: patch.cancellationWindowHours ?? currentSettings.cancellationWindowHours,
      require_deposit: patch.requireDeposit ?? currentSettings.requireDeposit,
      email_reminders: patch.emailReminders ?? currentSettings.emailReminders,
      sms_reminders: patch.smsReminders ?? currentSettings.smsReminders,
      hours: patch.hours ?? currentSettings.hours,
      updated_at: new Date().toISOString(),
    }).eq("business_id", currentBusiness.id);
    await refresh();
  }, [currentBusiness, currentSettings, refresh]);

  const value = useMemo<AppContextValue>(() => ({
    state,
    currentUser,
    currentBusiness,
    currentSettings,
    loading,
    configError,
    refresh,
    login,
    signup,
    logout,
    requestPasswordReset,
    updatePassword,
    bookAppointment,
    cancelAppointment,
    updateAppointmentStatus,
    addService,
    updateService,
    deleteService,
    addStaff,
    updateStaff,
    deleteStaff,
    clockStaff,
    updateClientNotes,
    updateBusinessProfile,
    updateBusinessSettings,
  }), [
    addService,
    addStaff,
    bookAppointment,
    cancelAppointment,
    configError,
    currentBusiness,
    currentSettings,
    currentUser,
    deleteService,
    deleteStaff,
    clockStaff,
    loading,
    login,
    logout,
    refresh,
    requestPasswordReset,
    updatePassword,
    signup,
    state,
    updateAppointmentStatus,
    updateBusinessProfile,
    updateBusinessSettings,
    updateClientNotes,
    updateService,
    updateStaff,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within AppProvider.");
  }
  return context;
}

export function useOwnerSnapshot(today: string) {
  const { state, currentBusiness } = useApp();

  return useMemo(() => {
    if (!currentBusiness) {
      return null;
    }

    const appointments = state.appointments.filter((entry) => entry.businessId === currentBusiness.id);
    const services = state.services.filter((entry) => entry.businessId === currentBusiness.id);
    const staff = state.staff.filter((entry) => entry.businessId === currentBusiness.id);
    const clients = state.clients.filter((entry) => entry.businessId === currentBusiness.id);

    return {
      appointments,
      services,
      staff,
      clients,
      views: appointments.map((appointment) => buildAppointmentView({ appointment, clients, services, staff })),
      stats: calculateOwnerStats({ appointments, services, clients, today }),
      monthlyRevenue: groupRevenueByMonth({ appointments, services }),
      topServices: topServices({ appointments, services }),
    };
  }, [currentBusiness, state.appointments, state.clients, state.services, state.staff, today]);
}

export function useStaffSnapshot(today: string) {
  const { state, currentBusiness, currentUser } = useApp();

  return useMemo(() => {
    if (!currentBusiness || !currentUser) {
      return null;
    }

    const staffMember = state.staff.find((entry) => entry.userId === currentUser.id);
    if (!staffMember) {
      return null;
    }

    const appointments = state.appointments
      .filter((entry) => entry.businessId === currentBusiness.id && entry.staffId === staffMember.id)
      .map((appointment) =>
        buildAppointmentView({
          appointment,
          clients: state.clients,
          services: state.services,
          staff: state.staff,
        }),
      )
      .sort((left, right) => `${left.date}-${left.startMinutes}`.localeCompare(`${right.date}-${right.startMinutes}`));

    const upcoming = appointments.filter((entry) => entry.status === "confirmed" || entry.status === "pending");
    const completed = appointments.filter((entry) => entry.status === "completed");

    return {
      staffMember,
      appointments,
      upcoming,
      completed,
      todayAppointments: upcoming.filter((entry) => entry.date === today),
      stats: {
        upcomingCount: upcoming.length,
        completedCount: completed.length,
        commissionTotal: completed.reduce((sum, entry) => sum + (entry.servicePrice * staffMember.commissionRate) / 100, 0),
        revenueHandled: completed.reduce((sum, entry) => sum + entry.servicePrice, 0),
      },
    };
  }, [currentBusiness, currentUser, state.appointments, state.clients, state.services, state.staff, today]);
}
